import { useState } from 'react';
import { formatMoney } from '../lib/currency';
import type { Addon, EventData, SponsorTier, TicketTier, VatMode } from '../lib/types';
import NumberField from './NumberField';

type Props = {
  data: EventData;
  currency: string;
  vatMode: VatMode;
  vatRate: number;
  // Templates without add-ons (e.g. meetup) hide the section entirely
  // and drop it from the breakdown chart.
  showAddons?: boolean;
  onChange: (patch: (d: EventData) => EventData) => void;
};

type PriceMode = 'min' | 'max';

export default function IncomesTab({
  data,
  currency,
  vatMode,
  vatRate,
  showAddons = true,
  onChange,
}: Props) {
  const [mode, setMode] = useState<PriceMode>('min');

  return (
    <div className="row-stack">
      <ModeToggle mode={mode} setMode={setMode} vatMode={vatMode} />
      <BreakdownCard
        data={data}
        currency={currency}
        mode={mode}
        vatMode={vatMode}
        vatRate={vatRate}
        showAddons={showAddons}
      />

      <div className="card-grid">
        <TicketsSection
          tickets={data.tickets}
          currency={currency}
          mode={mode}
          onChange={(tickets) => onChange((d) => ({ ...d, tickets }))}
        />
        <SponsorsSection
          sponsors={data.sponsors}
          currency={currency}
          mode={mode}
          onChange={(sponsors) => onChange((d) => ({ ...d, sponsors }))}
        />
      </div>

      {showAddons && (
        <AddonsSection
          addons={data.addons}
          currency={currency}
          onChange={(addons) => onChange((d) => ({ ...d, addons }))}
        />
      )}
    </div>
  );
}

function ModeToggle({
  mode,
  setMode,
  vatMode,
}: {
  mode: PriceMode;
  setMode: (m: PriceMode) => void;
  vatMode: VatMode;
}) {
  return (
    <div className="card scenario-bar">
      <div className="scenario-bar-header">
        <div className="muted">
          Price scenario — applies to tickets and sponsors · prices entered{' '}
          <strong>{vatMode === 'net' ? 'net of VAT' : 'gross'}</strong>
        </div>
        <div className="pill-tabs inline">
          <button
            className={`pill-tab ${mode === 'min' ? 'active' : ''}`}
            onClick={() => setMode('min')}
          >
            Min prices
          </button>
          <button
            className={`pill-tab ${mode === 'max' ? 'active' : ''}`}
            onClick={() => setMode('max')}
          >
            Max prices
          </button>
        </div>
      </div>
    </div>
  );
}

function BreakdownCard({
  data,
  currency,
  mode,
  vatMode,
  vatRate,
  showAddons,
}: {
  data: EventData;
  currency: string;
  mode: PriceMode;
  vatMode: VatMode;
  vatRate: number;
  showAddons: boolean;
}) {
  const tickets = data.tickets.reduce((acc, t) => {
    const p = mode === 'min' ? t.priceMin : t.priceMax;
    return acc + t.qty * p;
  }, 0);
  const sponsors = data.sponsors.reduce((a, s) => {
    const p = mode === 'min' ? s.priceMin : s.priceMax;
    return a + s.qty * p;
  }, 0);
  const addons = data.addons.reduce(
    (a, x) =>
      a +
      (x.enabled
        ? x.price * (x.multiplier && x.multiplier > 0 ? x.multiplier : 1)
        : 0),
    0,
  );
  const totalNet = tickets + sponsors + addons;
  const showVat = vatMode === 'gross';
  const vatTotal = showVat ? totalNet * vatRate : 0;
  const totalGross = totalNet + vatTotal;
  // Bar proportions are computed against gross total so both segments sum
  // correctly to 100%.
  const denom = showVat ? totalGross : totalNet;
  const pct = (n: number) => (denom > 0 ? Math.round((n / denom) * 100) : 0);

  return (
    <div className="card">
      <h2>
        Revenue breakdown ({mode === 'min' ? 'min prices' : 'max prices'})
        <span className="muted section-meta-tag">
          · {showVat ? `net + ${(vatRate * 100).toFixed(0)}% VAT` : 'net of VAT'}
        </span>
      </h2>
      <div className="breakdown">
        <BarRow
          cls="tickets"
          label="Tickets"
          netAmount={tickets}
          denom={denom}
          currency={currency}
          showVat={showVat}
          vatRate={vatRate}
        />
        <BarRow
          cls="sponsors"
          label="Sponsors"
          netAmount={sponsors}
          denom={denom}
          currency={currency}
          showVat={showVat}
          vatRate={vatRate}
        />
        {showAddons && (
          <BarRow
            cls="addons"
            label="Add-ons"
            netAmount={addons}
            denom={denom}
            currency={currency}
            showVat={showVat}
            vatRate={vatRate}
          />
        )}
      </div>

      {showVat ? (
        <div className="sum-bar sum-bar-stacked">
          <span>Total revenue ({mode === 'min' ? 'min' : 'max'})</span>
          <div className="sum-vat-breakdown">
            <span className="sum-net">Net {formatMoney(totalNet, currency)}</span>
            <span className="sum-sep">·</span>
            <span className="sum-vat">VAT {formatMoney(vatTotal, currency)}</span>
            <span className="sum-sep">·</span>
            <span className="total">Gross {formatMoney(totalGross, currency)}</span>
          </div>
        </div>
      ) : (
        <div className="sum-bar">
          <span>Total revenue ({mode === 'min' ? 'min' : 'max'})</span>
          <span className="total">{formatMoney(totalNet, currency)}</span>
        </div>
      )}

      <div className="muted mt-6">
        Tickets: {pct(tickets * (showVat ? 1 + vatRate : 1))}% · Sponsors:{' '}
        {pct(sponsors * (showVat ? 1 + vatRate : 1))}%
        {showAddons && (
          <>
            {' · Add-ons: '}
            {pct(addons * (showVat ? 1 + vatRate : 1))}%
          </>
        )}
      </div>
    </div>
  );
}

function BarRow({
  cls,
  label,
  netAmount,
  denom,
  currency,
  showVat,
  vatRate,
}: {
  cls: string;
  label: string;
  netAmount: number;
  denom: number;
  currency: string;
  showVat: boolean;
  vatRate: number;
}) {
  const vat = showVat ? netAmount * vatRate : 0;
  const gross = netAmount + vat;
  const netPct = denom > 0 ? (netAmount / denom) * 100 : 0;
  const vatPct = denom > 0 ? (vat / denom) * 100 : 0;
  const n = Math.max(0, Math.min(100, Math.round(netPct)));
  const v = Math.max(0, Math.min(100, Math.round(vatPct)));

  return (
    <div className="breakdown-row">
      <div className="top">
        <span>{label}</span>
        <span className="amt">
          {showVat ? (
            <>
              <span className="muted">net</span> {formatMoney(netAmount, currency)}
              <span className="muted"> · VAT</span> {formatMoney(vat, currency)}
              <span className="muted"> · gross</span> {formatMoney(gross, currency)}
            </>
          ) : (
            formatMoney(netAmount, currency)
          )}
        </span>
      </div>
      <div className={`bar ${cls} ${showVat ? 'has-vat' : ''}`}>
        <div className={`bar-net w-pct-${n}`} />
        {showVat && <div className={`bar-vat w-pct-${v}`} />}
      </div>
    </div>
  );
}

function TicketsSection({
  tickets,
  currency,
  mode,
  onChange,
}: {
  tickets: TicketTier[];
  currency: string;
  mode: PriceMode;
  onChange: (t: TicketTier[]) => void;
}) {
  function patch(idx: number, p: Partial<TicketTier>) {
    onChange(tickets.map((t, i) => (i === idx ? { ...t, ...p } : t)));
  }
  function add() {
    onChange([
      ...tickets,
      {
        id: `t_${Math.random().toString(36).slice(2, 8)}`,
        name: 'New tier',
        qty: 0,
        priceMin: 0,
        priceMax: 0,
      },
    ]);
  }
  function remove(idx: number) {
    onChange(tickets.filter((_, i) => i !== idx));
  }

  const total = tickets.reduce((acc, t) => {
    const p = mode === 'min' ? t.priceMin : t.priceMax;
    return acc + t.qty * p;
  }, 0);
  const totalCount = tickets.reduce((a, t) => a + t.qty, 0);

  return (
    <div className="card">
      <h2>Tickets</h2>

      <div className="row-stack">
        {tickets.map((t, idx) => (
          <div key={t.id} className="tier-row">
            <div className="row center">
              <input
                className="input"
                value={t.name}
                onChange={(e) => patch(idx, { name: e.target.value })}
              />
              <button
                className="btn sm danger"
                onClick={() => remove(idx)}
                title="Remove tier"
              >
                ×
              </button>
            </div>
            <div className="tier-fields">
              <Labeled label="Qty">
                <NumberField
                  value={t.qty}
                  onChange={(n) => patch(idx, { qty: n })}
                />
              </Labeled>
              <Labeled label="Price min">
                <NumberField
                  value={t.priceMin}
                  step={1}
                  onChange={(n) => patch(idx, { priceMin: n })}
                />
              </Labeled>
              <Labeled label="Price max">
                <NumberField
                  value={t.priceMax}
                  step={1}
                  onChange={(n) => patch(idx, { priceMax: n })}
                />
              </Labeled>
            </div>
          </div>
        ))}
      </div>

      <div className="cost-add">
        <button className="btn ghost sm" onClick={add}>
          + Add tier
        </button>
      </div>

      <div className="sum-bar">
        <span>
          Σ Tickets ({totalCount} {totalCount === 1 ? 'ticket' : 'tickets'},{' '}
          {mode})
        </span>
        <span className="total">{formatMoney(total, currency)}</span>
      </div>
    </div>
  );
}

function SponsorsSection({
  sponsors,
  currency,
  mode,
  onChange,
}: {
  sponsors: SponsorTier[];
  currency: string;
  mode: PriceMode;
  onChange: (s: SponsorTier[]) => void;
}) {
  function patch(idx: number, p: Partial<SponsorTier>) {
    onChange(sponsors.map((s, i) => (i === idx ? { ...s, ...p } : s)));
  }
  function add() {
    onChange([
      ...sponsors,
      {
        id: `s_${Math.random().toString(36).slice(2, 8)}`,
        name: 'New tier',
        qty: 0,
        priceMin: 0,
        priceMax: 0,
      },
    ]);
  }
  function remove(idx: number) {
    onChange(sponsors.filter((_, i) => i !== idx));
  }

  const total = sponsors.reduce((a, s) => {
    const p = mode === 'min' ? s.priceMin : s.priceMax;
    return a + s.qty * p;
  }, 0);
  const totalCount = sponsors.reduce((a, s) => a + s.qty, 0);

  return (
    <div className="card">
      <h2>Sponsors</h2>

      <div className="row-stack">
        {sponsors.map((s, idx) => (
          <div key={s.id} className="tier-row">
            <div className="row">
              <input
                className="input"
                value={s.name}
                onChange={(e) => patch(idx, { name: e.target.value })}
              />
              <button
                className="btn sm danger"
                onClick={() => remove(idx)}
                title="Remove tier"
              >
                ×
              </button>
            </div>
            <div className="tier-fields">
              <Labeled label="Qty">
                <NumberField
                  value={s.qty}
                  onChange={(n) => patch(idx, { qty: n })}
                />
              </Labeled>
              <Labeled label="Price min">
                <NumberField
                  value={s.priceMin}
                  onChange={(n) => patch(idx, { priceMin: n })}
                />
              </Labeled>
              <Labeled label="Price max">
                <NumberField
                  value={s.priceMax}
                  onChange={(n) => patch(idx, { priceMax: n })}
                />
              </Labeled>
            </div>
          </div>
        ))}
      </div>

      <div className="cost-add">
        <button className="btn ghost sm" onClick={add}>
          + Add tier
        </button>
      </div>

      <div className="sum-bar">
        <span>
          Σ Sponsors ({totalCount}{' '}
          {totalCount === 1 ? 'sponsor' : 'sponsors'}, {mode})
        </span>
        <span className="total">{formatMoney(total, currency)}</span>
      </div>
    </div>
  );
}

function AddonsSection({
  addons,
  currency,
  onChange,
}: {
  addons: Addon[];
  currency: string;
  onChange: (a: Addon[]) => void;
}) {
  function patch(idx: number, p: Partial<Addon>) {
    onChange(addons.map((a, i) => (i === idx ? { ...a, ...p } : a)));
  }
  function add() {
    onChange([
      ...addons,
      {
        id: `a_${Math.random().toString(36).slice(2, 8)}`,
        name: 'New add-on',
        enabled: true,
        price: 0,
      },
    ]);
  }
  function remove(idx: number) {
    onChange(addons.filter((_, i) => i !== idx));
  }
  const total = addons.reduce(
    (acc, a) =>
      acc +
      (a.enabled
        ? a.price * (a.multiplier && a.multiplier > 0 ? a.multiplier : 1)
        : 0),
    0,
  );
  const enabledCount = addons.filter((a) => a.enabled).length;

  return (
    <div className="card">
      <div className="section-head">
        <h2>Add-ons</h2>
        <button className="btn ghost sm" onClick={add}>
          + Add
        </button>
      </div>
      <div className="addons-grid">
        {addons.map((a, idx) => (
          <div key={a.id} className={`addon-card ${a.enabled ? 'on' : ''}`}>
            <div className="addon-head">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={a.enabled}
                  onChange={(e) => patch(idx, { enabled: e.target.checked })}
                />
                <span className="switch-slider" />
              </label>
              <input
                className="input addon-name-input"
                value={a.name}
                onChange={(e) => patch(idx, { name: e.target.value })}
              />
              <button
                className="btn sm danger"
                onClick={() => remove(idx)}
                title="Remove"
              >
                ×
              </button>
            </div>
            <div className="addon-row">
              <span>Price</span>
              <NumberField
                value={a.price}
                onChange={(n) => patch(idx, { price: n })}
              />
            </div>
            {a.multiplierLabel != null && (
              <div className="addon-row">
                <span>{a.multiplierLabel}</span>
                <NumberField
                  value={a.multiplier ?? 1}
                  onChange={(n) => patch(idx, { multiplier: n })}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="sum-bar">
        <span>Σ Add-ons ({enabledCount} active)</span>
        <span className="total">{formatMoney(total, currency)}</span>
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: any }) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
    </div>
  );
}
